import { Text } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { z } from 'zod';

import {
  ColorScheme,
  FontWeights,
} from '~/packages/common-frontend/theme/types';
import {
  fontSizes,
  fontWeights,
} from '~/packages/common-frontend/theme/typography';
import { useSimpleColorMode } from '~/packages/common-frontend/theme/utils';

// Types
export const TYPOGRAPHY_VARIANTS = [
  'page-title',
  'section-title',
  'section-subtitle',
  'base',
  'base-muted',
  'detail',
  'detail-muted',
] as const;
export const TypographyVariantStruct = z
  .enum(TYPOGRAPHY_VARIANTS)
  .default('base');
export type TypographyVariant = z.infer<typeof TypographyVariantStruct>;

// Exports
type TypographyProps = {
  children: React.ReactNode;
  colorScheme?: ColorScheme;
  inline?: boolean;
  fontWeight?: FontWeights;
  trim?: boolean;
  variant?: TypographyVariant;
  TextProps?: React.ComponentProps<typeof Text>;
};
/**
 * Variants:
 * - `page-title`:       The primary page header. There should only be one of these on any given route
 * - `section-title`:    On pages with section, use this. Ex. page sections, modal and drawer titles, etc.
 * - `section-subtitle`: On pages where sections have subsections, use this. Ex. chart title when next to other charts in a section
 * - `base`:             (default) Font style used for general page content. Ex. paragraphs, lists, buttons, inputs, etc.
 * - `base-muted`:       Same as "base", but slightly lighter. Use when you want to de-emphasize text.
 * - `detail`:           Smaller font style for secondary page text content. Ex. tooltips, input error messages, etc.
 * - `detail-muted`:     Same as "detail", but slightly lighter. Use when you want to de-emphasize text.
 *
 * Pass `trim=true` to get rid of default margins
 *
 * Pass `inline=true` to render text as `display: inline`. Also swaps out `<p>` tags for `<span>`s for non-headings
 *
 * NOTE: `colorScheme` has no effect on headings. They are always rendered as gray.950/white
 */

export const Typography = React.forwardRef<
  HTMLParagraphElement,
  TypographyProps
>((props: TypographyProps, ref) => {
  const {
    children,
    colorScheme = 'gray',
    inline = false,
    fontWeight,
    trim = false,
    variant = 'base',
    TextProps,
  } = props;

  const mode = useSimpleColorMode();

  const variantProps = useMemo(() => {
    switch (variant) {
      case 'page-title': {
        return {
          as: 'h1',
          color: mode('gray.950', 'white'),
          display: inline ? 'inline' : 'block',
          fontSize: fontSizes['4xl'].fontSize,
          fontWeight: fontWeights.bold,
          lineHeight: fontSizes['4xl'].lineHeight,
          marginBottom: trim ? 0 : '1.5rem',
        } as const;
      }
      case 'section-title': {
        return {
          as: 'h2',
          color: mode('gray.950', 'white'),
          display: inline ? 'inline' : 'block',
          fontSize: fontSizes['2xl'].fontSize,
          fontWeight: fontWeights.semibold,
          lineHeight: fontSizes['2xl'].lineHeight,
          marginBottom: trim ? 0 : '1.5rem',
        } as const;
      }
      case 'section-subtitle': {
        return {
          as: 'h3',
          color: mode('gray.950', 'white'),
          display: inline ? 'inline' : 'block',
          fontSize: fontSizes.xl.fontSize,
          fontWeight: fontWeights.semibold,
          lineHeight: fontSizes.xl.lineHeight,
          marginBottom: trim ? 0 : '1.5rem',
        } as const;
      }
      case 'base': {
        return {
          as: inline ? 'span' : 'p',
          color: mode(`${colorScheme}.800`, `${colorScheme}.100`),
          fontSize: fontSizes.base.fontSize,
          fontWeight: fontWeight ?? fontWeights.normal,
          lineHeight: fontSizes.base.lineHeight,
        } as const;
      }
      case 'base-muted': {
        return {
          as: inline ? 'span' : 'p',
          color: mode(`${colorScheme}.500`, `${colorScheme}.300`),
          fontSize: fontSizes.base.fontSize,
          fontWeight: fontWeight ?? fontWeights.normal,
          lineHeight: fontSizes.base.lineHeight,
        } as const;
      }
      case 'detail': {
        return {
          as: inline ? 'span' : 'p',
          color: mode(`${colorScheme}.800`, `${colorScheme}.100`),
          fontSize: fontSizes.sm.fontSize,
          fontWeight: fontWeight ?? fontWeights.normal,
          lineHeight: fontSizes.sm.lineHeight,
        } as const;
      }
      case 'detail-muted': {
        return {
          as: inline ? 'span' : 'p',
          color: mode(`${colorScheme}.500`, `${colorScheme}.300`),
          fontSize: fontSizes.sm.fontSize,
          fontWeight: fontWeight ?? fontWeights.normal,
          lineHeight: fontSizes.sm.lineHeight,
        } as const;
      }
      default: {
        throw Error(`Invalid <Typography /> variant[${variant}]`);
      }
    }
  }, [colorScheme, fontWeight, inline, mode, trim, variant]);

  return (
    <Text ref={ref} {...variantProps} {...TextProps}>
      {children}
    </Text>
  );
});
Typography.displayName = 'Typography';
